import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Skeleton } from "@sagot/ui";
import type { ComponentProps, ReactNode } from "react";
import { AnnouncementCard } from "./announcement-card";
import type { Announcement } from "./model";

type CardProps = Omit<ComponentProps<typeof AnnouncementCard>, "item">;
interface Page {
  items: Announcement[];
  next_cursor: string | null;
}

interface ListProps {
  list: UseInfiniteQueryResult<InfiniteData<Page>>;
  items: Announcement[];
  filtered: boolean;
  emptyAction: ReactNode;
  cardProps: CardProps;
}

function CardSkeleton() {
  return (
    <div className="card" aria-hidden="true">
      <div className="card-body flex flex-col gap-5 lg:flex-row">
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-[180px]" />
          <Skeleton className="h-5 w-[360px] max-w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <div className="flex gap-1.5 pt-1">
            <Skeleton className="h-5 w-[80px]" />
            <Skeleton className="h-5 w-[96px]" />
            <Skeleton className="h-5 w-[96px]" />
          </div>
        </div>
        <div className="shrink-0 space-y-2 lg:w-80">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="ml-auto h-8 w-[180px]" />
        </div>
      </div>
    </div>
  );
}

/** Card list with skeleton, error (retry), illustrated empty state and "Load more". */
export function AnnouncementList({ list, items, filtered, emptyAction, cardProps }: ListProps) {
  const { t } = useTranslation();
  if (list.isPending) {
    return (
      <div className="space-y-4">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }
  if (list.isError) {
    return (
      <div className="card">
        <ErrorState title={t("engage.announcements.loadError")} onRetry={() => void list.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="megaphone"
          title={t("engage.announcements.emptyTitle")}
          description={filtered ? t("engage.announcements.emptySearchBody") : t("engage.announcements.emptyBody")}
          action={emptyAction}
        />
      </div>
    );
  }
  return (
    <div className="space-y-4" aria-busy={list.isFetching}>
      {items.map((item) => (
        <AnnouncementCard key={item.id} item={item} {...cardProps} />
      ))}
      {list.hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {t("engage.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
